<script setup lang="ts">
// Per-thread CPU load over the selected window: one row per logical core,
// time left to right on the same axis as the sparklines, darker = busier,
// the bad colour at 80 % and above (the machine alarm threshold). Drawn on a
// canvas: an hour of one-second samples for 64 threads is 230k cells.
import { onMounted, ref, watch } from 'vue'
import { clock, tipPos } from '../util'

const props = defineProps<{ times: number[]; cores: number[][]; from: number; to: number }>()
const canvas = ref<HTMLCanvasElement | null>(null)
const rowH = 4
const w = 600
const hover = ref<{ x: number; y: number; text: string } | null>(null)

// CSS variables resolved to rgb so the canvas follows the theme
function rgb(el: HTMLElement, v: string): [number, number, number] {
    const probe = document.createElement('span')
    probe.style.color = `var(${v})`
    el.appendChild(probe)
    const m = getComputedStyle(probe).color.match(/\d+/g)?.map(Number) ?? [128, 128, 128]
    probe.remove()
    return [m[0] ?? 128, m[1] ?? 128, m[2] ?? 128]
}

function draw() {
    const c = canvas.value
    if (!c) return
    const threads = Math.max(0, ...props.cores.map((r) => r.length))
    c.width = w
    c.height = Math.max(1, threads * rowH)
    const ctx = c.getContext('2d')!
    ctx.clearRect(0, 0, c.width, c.height)
    const [ar, ag, ab] = rgb(c.parentElement!, '--accent')
    const [br, bg, bb] = rgb(c.parentElement!, '--bad')
    const span = Math.max(1, props.to - props.from)
    const xOf = (tSec: number) => ((tSec * 1000 - props.from) / span) * w
    const t = props.times
    const step = t.length > 1 ? Math.max(1, t[1]! - t[0]!) : 1
    for (let i = 0; i < t.length; i++) {
        const x0 = xOf(t[i]!)
        // up to the next sample, but never across a gap in the data
        const next = i + 1 < t.length && t[i + 1]! - t[i]! <= step * 3 ? t[i + 1]! : t[i]! + step
        const cw = Math.max(1, xOf(next) - x0)
        if (x0 + cw < 0 || x0 > w) continue
        const row = props.cores[i] ?? []
        for (let k = 0; k < row.length; k++) {
            const v = Math.min(100, Math.max(0, row[k]!))
            ctx.fillStyle =
                v >= 80 ? `rgb(${br},${bg},${bb})` : `rgba(${ar},${ag},${ab},${(0.08 + (0.92 * v) / 80).toFixed(3)})`
            ctx.fillRect(x0, k * rowH, cw, rowH - 1)
        }
    }
}
onMounted(draw)
watch(() => [props.times, props.cores, props.from, props.to], draw)

function move(e: MouseEvent) {
    const c = canvas.value!
    const r = c.getBoundingClientRect()
    const tMs = props.from + ((e.clientX - r.left) / r.width) * (props.to - props.from)
    const core = Math.floor(((e.clientY - r.top) / r.height) * (c.height / rowH))
    let best = -1,
        bd = Infinity
    props.times.forEach((t, i) => {
        const d = Math.abs(t * 1000 - tMs)
        if (d < bd) {
            bd = d
            best = i
        }
    })
    const v = best >= 0 ? props.cores[best]?.[core] : undefined
    hover.value =
        v === undefined
            ? null
            : {
                  ...tipPos(e, 200, 40),
                  text: `${clock(new Date(props.times[best]! * 1000), true)} · thread ${core} · ${v.toFixed(0)} %`,
              }
}
</script>

<template>
    <div class="relative mt-1.5 mb-2.5">
        <div class="mb-0.75 text-xs text-fg-3">
            cpu per thread · {{ Math.max(0, ...cores.map((r) => r.length)) }} threads
        </div>
        <canvas
            ref="canvas"
            class="block w-full rounded-[2px] bg-bg-2 [image-rendering:pixelated]"
            @mousemove="move"
            @mouseleave="hover = null"
        />
        <div
            v-if="hover"
            class="pointer-events-none fixed z-30 rounded-md border border-line-strong bg-bg-3 px-1.75 py-0.75 font-mono text-xs whitespace-nowrap tabular-nums"
            :style="{ left: hover.x + 'px', top: hover.y + 'px' }"
        >
            {{ hover.text }}
        </div>
    </div>
</template>
